import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import {
  Home as IconHome,
  AlertCircle as IconAlertCircle,
  RefreshCcw as IconSync,
} from 'react-feather'
import {
  Input,
  Row,
  InputGroup,
  InputGroupAddon,
  Alert as ReactAlert,
  Button,
  Spinner,
  CardTitle,
} from 'reactstrap'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { Formik, Form, useFormikContext } from 'formik'
import { isBefore } from 'date-fns'
import _ from 'lodash'

import useAxios from 'axios-hooks'
import Field from './commons/Field'
import { Col, LoadableCard, Show } from '~/components/commons'
import Breadcrumb from '~/components/Breadcrumb'
import Select from '~/components/Select'
import DateTimePicker from '~/components/DateTimePicker'
import UploadPhoto from '~/components/ecto/UploadPhoto'
import Checkbox from '~/components/Checkbox'
import * as yup from 'yup'
import MultiSelect from '~/components/MultiSelect'
import { shade } from 'polished'
import { useSelector } from 'react-redux'
import { Alert } from '~/components/Alert'
import { SUPER_ADMIN } from '~/constants/roles'

const initialValues = {
  name: '',
  accountId: '',
  expirationDate: null,
  website: '',
  brandedKeywords: '',
  brandName: '',
  gmbAccountId: '',
  avatarImage: null,
  avatarLink: null,
  canImportKeywords: false,
}

const validationSchema = yup.object().shape({
  name: yup.string().label('Name').required('This field is required'),
  accountId: yup.string().label('Account').required('This field is required'),
  gmbAccountId: yup
    .string()
    .label('GmB Account')
    .required('This field is required'),
})

function useOptions({ endpoint, getData, buildOption, config }) {
  const [requestState, refetch] = useAxios(endpoint, config)

  const options = useMemo(() => {
    const { data, loading, error } = requestState
    if (!data || loading || error) {
      return []
    }
    return getData(data)?.map(item => {
      return buildOption(item)
    })
  }, [requestState, getData, buildOption])
  return [options, refetch, requestState]
}

function NewProjectForm({
  loading,
  accounts,
  googleAccounts,
  users,
  isUpdate,
  selectedUsers,
  setSelectedAccount,
  gmbLoading,
  accLoading,
  usersLoading,
  canImportKeywords,
  setCanImportKeywords,
  canAccountImport,
  setCanAccountImport,
  hiddenProjects,
}) {
  const { setFieldValue, initialValues } = useFormikContext()
  const [datetimeSchedule, setDatetimeSchedule] = useState(new Date())
  const [selects, setSelects] = useState()

  const {
    expirationDate: initialDate,
    avatarImage: initialImage,
  } = initialValues

  const isScheduleInvalid = useMemo(
    () => !!datetimeSchedule && isBefore(datetimeSchedule, new Date()),
    [datetimeSchedule]
  )

  function setExpirationDate(value) {
    setFieldValue('expirationDate', value, false)
  }

  const handleFilterValueChange = useCallback(
    (filter, options) => {
      setFieldValue('usersIds', options)
      setSelects(old => ({ ...old, [filter]: options }))
    },
    [setFieldValue, setSelects]
  )

  useEffect(() => {
    handleFilterValueChange('usersIds', selectedUsers)
    setDatetimeSchedule(initialDate)
  }, [selectedUsers, initialDate, handleFilterValueChange])

  function handleChangeSchedule(date) {
    const selected = date?.[0]
    if (!selected) return
    setDatetimeSchedule(selected)
    setExpirationDate(selected)
  }

  function resetSchedule() {
    setDatetimeSchedule('')
    setFieldValue(null)
  }

  function handleSelectedAccount(selectedAccount, helpers) {
    setCanAccountImport(Boolean(selectedAccount.importKeywords))
    setSelectedAccount(selectedAccount.value)
    helpers.setValue(selectedAccount)
    setSelects(old => ({ ...old, usersIds: [] }))
  }

  const customSelect = props => {
    return (
      <Select
        maxMenuHeight={180}
        {...props}
        controlSize={'39px'}
        styles={{
          menu: provided => ({
            ...provided,
            zIndex: 9999,
          }),
        }}
      />
    )
  }

  return (
    <FormContainer>
      <Row>
        <Col lg="4" className="px-1">
          <Field name="name" label="Project Name" render={Input} required />
        </Col>
        <Col lg="4" className="px-1">
          <Field
            name="accountId"
            label="Account"
            render={StyledSelect}
            placeholder="Select a account"
            onChange={(value, helpers) => handleSelectedAccount(value, helpers)}
            errorExtractor={error => error.value}
            options={accounts}
            required
            isLoading={accLoading}
            isDisabled={accLoading}
          />
        </Col>
        <Col lg="4" className="px-1">
          <label className="font-small-3" htmlFor="expirate-date">
            <FormattedMessage id="Expirate date*" />
          </label>
          <InputGroup style={{ flexWrap: 'nowrap' }}>
            <DateTimePicker
              id="expirate-date"
              style={{
                display: 'flex',
                flex: 1,
              }}
              className="text-center"
              showIcon
              value={datetimeSchedule}
              onChange={handleChangeSchedule}
              invalid={isScheduleInvalid}
              options={{
                mode: 'single',
                minDate: new Date(),
              }}
            />
            <InputGroupAddon addonType="append">
              <Button
                style={{ zIndex: 0 }}
                onClick={resetSchedule}
                type="button"
                color="warning"
              >
                <FormattedMessage id="Clear" />
              </Button>
            </InputGroupAddon>
          </InputGroup>
          <ReactAlert
            className="mt-1"
            color="danger"
            isOpen={isScheduleInvalid}
          >
            <IconAlertCircle size={15} />{' '}
            <span>
              <FormattedMessage id="You can only schedule a post to happen in the future" />
            </span>
          </ReactAlert>
        </Col>
      </Row>
      <Row>
        <Col lg="4" md="12" className="px-1">
          <Field
            name="website"
            label="Website"
            placeholder={'https://example.com'}
            render={Input}
          />
        </Col>
        <Col lg="4" md="12" className="px-1">
          <Field
            name="brandedKeywords"
            label="Branded Keywords"
            render={Input}
            helperText="Separate keywords with commas eg: keyword1, keyword2, keyword3"
          />
        </Col>
        <Col lg="4" md="12" className="px-1">
          <Field
            name="brandName"
            label="Brand Name"
            render={Input}
            helperText="Name to be used in Byo GG links instead of the project name"
          />
        </Col>
      </Row>
      {canAccountImport && (
        <Row className="ml-0">
          <Checkbox
            color="primary"
            label="Allow import keywords"
            size="md"
            checked={canImportKeywords}
            onChange={() => setCanImportKeywords(prev => !prev)}
          />
        </Row>
      )}
      <Row>
        <Col lg="6" md="12" className="px-1">
          <Field
            name="gmbAccountId"
            label="Google My Business Account"
            className="mb-2"
            render={customSelect}
            placeholder="Select a Google My Business account"
            onChange={(selected, helpers) => helpers.setValue(selected)}
            errorExtractor={error => error.value}
            options={googleAccounts}
            required
            isLoading={gmbLoading}
            isDisabled={gmbLoading}
          />
          <Show condition={!!users?.length}>
            <MultiSelect
              label="Select one or more users for the project"
              uniqueKey="usersIds"
              className="mb-2"
              placeholder="Users"
              options={users}
              value={selects?.usersIds}
              onChange={options => handleFilterValueChange('usersIds', options)}
              isDisabled={usersLoading}
              isLoading={usersLoading}
            />
          </Show>
          <Show condition={hiddenProjects?.length}>
            <div style={{ fontWeight: 700 }}>
              <FormattedMessage id="Projects unified under this project" />
            </div>
            {hiddenProjects?.map(project => (
              <div key={project.gmbAccountId}>
                {project.name} - {project.gmbAccountId}
              </div>
            ))}
          </Show>
        </Col>
        <Col lg="6" md="12" className="px-1">
          <UploadPhoto
            name="avatarImage"
            label="Project logo:"
            onChange={v => setFieldValue('avatarImage', v)}
            showHelperText
            helperText={`Allowed JPG or PNG. Max size of 800kB.`}
            helperText2={`This photo visible only in Locasty's dashboard.`}
            initialImage={initialImage}
          />
        </Col>
      </Row>
      <div className="d-flex align-items-end mt-2 w-100 w-md-auto">
        <Button
          type="submit"
          color="primary"
          className="mr-0 mr-md-2 d-md-flex"
        >
          {loading ? (
            <Spinner color="white" size="sm" />
          ) : (
            <FormattedMessage
              id={isUpdate ? 'Update Project' : 'Create New Project'}
            />
          )}
        </Button>
      </div>
    </FormContainer>
  )
}

function CreateNewProject() {
  const [alertVisible, setAlertVisible] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const [isImports, setIsImports] = useState(false)
  const history = useHistory()
  const [selectedAccount, setSelectedAccount] = useState()
  const [selectedUsers, setSelectedUsers] = useState()
  const [formData, setFormData] = useState(null)
  const [isUpdate, setIsUpdate] = useState(false)
  const [autoImport, setAutoImport] = useState(false)
  const [importingLocation, setImportingLocation] = useState(false)
  const [projectDataLoad, setProjectDataLoad] = useState(false)
  const [canImportKeywords, setCanImportKeywords] = useState(false)
  const [canAccountImport, setCanAccountImport] = useState(false)
  const [hiddenProjects, setHiddenProjects] = useState([])
  const { projectId } = useParams()

  function openModal(importing) {
    if (importing) setIsImports(true)
    setModalMessage(
      importing
        ? 'Locations imported succesfully!'
        : `Project successfully ${isUpdate ? 'updated!' : 'created!'}`
    )
    setAlertVisible(true)
  }

  function closeSucessImportsAlert() {
    setAlertVisible(false)
    setIsImports(false)
  }

  const userProfile = useSelector(state => state.user?.profile)
  const isAppAdmin = userProfile?.role === SUPER_ADMIN

  const [, createProject] = useAxios(
    {
      url: '/projects',
      method: 'POST',
    },
    { manual: true }
  )
  const [, updateProject] = useAxios(
    {
      url: `/projects/${projectId}`,
      method: 'PATCH',
    },
    { manual: true }
  )

  const [, uploadFile] = useAxios(
    {
      url: '/upload',
      method: 'POST',
    },
    { manual: true }
  )

  const [, importLocationAdmin] = useAxios(
    {
      baseURL: process.env.REACT_APP_IMPORTER_API_URL,
      method: 'POST',
    },
    { manual: true }
  )

  const [accounts, , { loading: accLoading }] = useOptions({
    endpoint: '/admin/accounts',
    getData: data => data,
    buildOption: item => ({
      label: item.name,
      value: item.id,
      importKeywords: item.importKeywords,
    }),
  })

  const [
    gmbAccounts,
    fetchGmbAccounts,
    { loading: gmbAccountsLoading },
  ] = useOptions({
    endpoint: '/admin/gmb-accounts',
    getData: data => data,
    buildOption: item => ({
      label: `${item.gmbAccountId} - ${item.accountName}`,
      value: item.gmbAccountId,
    }),
  })

  const [users, , { loading: usersLoad }] = useOptions({
    endpoint: `/accounts/${selectedAccount}/users`,
    getData: data => data,
    buildOption: user => ({
      label: user.name,
      value: [user.id],
    }),
    config: { useCache: false, manual: !selectedAccount },
  })

  const [, fetchProjectData] = useAxios(`/projects/${projectId}`, {
    manual: true,
  })

  const fillProjectData = useCallback(async () => {
    setIsUpdate(true)
    setProjectDataLoad(true)
    const gmbAccounts = await fetchGmbAccounts().then(({ data }) =>
      data?.map(el => ({
        label: `${el.accountName} - ${el.name.split('/')[1]}`,
        value: el.name.split('/')[1],
      }))
    )
    const { data } = await fetchProjectData()
    const project = {
      name: data.isUnified
        ? data.name.replace('.INTERNAL - ', '')
        : data.name || '',
      accountId: { label: data.account.name, value: data.account.id } || '',
      expirationDate: data.expirationDate,
      website: data.website || '',
      brandedKeywords: data.brandedKeywords || '',
      brandName: data.brandName || '',
      gmbAccountId:
        gmbAccounts?.find(acc => acc.value === data.gmbAccountId) || '',
      avatarImage: data.avatar,
      avatarLink: null,
      canImportKeywords: data.canImportKeywords || false,
    }
    setHiddenProjects(data.hiddenProjects)
    setCanImportKeywords(data?.canImportKeywords || false)
    setCanAccountImport(Boolean(data.account.importKeywords))
    setSelectedAccount(data.account.id)
    setSelectedUsers(
      data.users?.map(user => ({ label: user.name, value: [user.id] }))
    )
    setFormData(project)
    setProjectDataLoad(false)
  }, [fetchGmbAccounts, fetchProjectData])

  useEffect(() => {
    if (projectId) {
      fillProjectData()
    }
  }, [projectId, fillProjectData, setFormData])

  async function handleSubmit(form, canImportKeywords) {
    let uploadedImageLink
    const {
      name,
      accountId,
      expirationDate,
      website,
      brandedKeywords,
      brandName,
      gmbAccountId,
      avatarImage,
      usersIds,
    } = form

    if (_.isObject(avatarImage)) {
      const fd = new FormData()
      fd.append('file', avatarImage)
      await uploadFile({ data: fd })
        .then(({ data }) => {
          uploadedImageLink = data.uri
        })
        .catch(e => {
          toast.error('An error occurred during upload image! Try again later.')
          console.error(e)
          throw new Error()
        })
    }

    try {
      var formData = {
        name,
        accountId: accountId?.value,
        expirationDate,
        website,
        brandedKeywords,
        brandName,
        usersIds: usersIds?.map(user => user.value[0]),
        gmbAccountId: gmbAccountId?.value,
        avatar: uploadedImageLink || avatarImage,
        canImportKeywords,
      }
      const submitAction = isUpdate ? updateProject : createProject

      await submitAction({ data: formData })
      openModal(false)
    } catch (error) {
      console.error('Error creating project', error)
      const { data } = error.response
      toast.error(data.error)
    }
  }

  const importLocation = async () => {
    setImportingLocation(true)
    importLocationAdmin({
      url: `/tasks/locations/import/${formData.gmbAccountId?.value}`,
    })
      .then(() => openModal(true))
      .catch(e => {
        setImportingLocation(false)
        toast.error('Error on import location')
        console.error(e)
      })
  }

  return (
    <>
      <Alert
        type="success"
        visible={alertVisible}
        onClose={() =>
          isImports ? closeSucessImportsAlert() : history.push('/projects')
        }
        {...(isImports
          ? {}
          : {
              onCancel: () => setAlertVisible(false),
              hasCancelBtn: true,
              cancelBtnText: 'Close',
            })}
        confirmBtnText={isImports ? 'Close' : 'Go to Projects'}
        body={modalMessage}
        title="Success"
      />
      <Col>
        <Row className="px-2 flex-column flex-md-row">
          <Col>
            <Breadcrumb
              className="mb-2"
              paths={[
                {
                  render: () => (
                    <Link className="d-flex align-items-center" to="/">
                      <IconHome size={20} />
                    </Link>
                  ),
                },
                {
                  render: () => (
                    <Link className="d-flex align-items-center" to="/projects">
                      <FormattedMessage id="Projects" />
                    </Link>
                  ),
                },
                { text: isUpdate ? 'Update Project' : 'Create New Project' },
              ]}
            />
          </Col>
          <Show condition={isAppAdmin && isUpdate}>
            <Col className="d-flex justify-content-end align-items-center">
              <ButtonSync
                to="/projects/new"
                onClick={importLocation}
                disabled={importingLocation}
                className="mb-2 mb-md-0"
              >
                <IconSync height={18} />
                Sync
              </ButtonSync>
            </Col>
          </Show>
        </Row>

        <LoadableCard
          loading={projectDataLoad}
          style={{ padding: '30px 40px' }}
        >
          <CardTitle className="mb-2">
            <FormattedMessage
              id={isUpdate ? 'Update Project' : 'Create New Project'}
            />
          </CardTitle>
          <Formik
            initialValues={formData || initialValues}
            onSubmit={form => handleSubmit(form, canImportKeywords)}
            validationSchema={validationSchema}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
            enableReinitialize
          >
            {() => (
              <Form>
                <NewProjectForm
                  loading={false}
                  users={users}
                  accounts={accounts}
                  googleAccounts={gmbAccounts}
                  selectedUsers={selectedUsers}
                  isUpdate={isUpdate}
                  setSelectedAccount={setSelectedAccount}
                  autoImport={autoImport}
                  setAutoImport={setAutoImport}
                  gmbLoading={gmbAccountsLoading}
                  accLoading={accLoading}
                  usersLoading={usersLoad}
                  canImportKeywords={canImportKeywords}
                  setCanImportKeywords={setCanImportKeywords}
                  canAccountImport={canAccountImport}
                  setCanAccountImport={setCanAccountImport}
                  hiddenProjects={hiddenProjects}
                />
              </Form>
            )}
          </Formik>
        </LoadableCard>
      </Col>
    </>
  )
}

export const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

export const StyledSelect = styled(Select)`
  > div {
    min-height: 39px;
  }
`

const ButtonSync = styled(Button)`
  font-size: 18px;
  font-weight: 500;
  color: white;
  background-color: #fe9f42 !important;
  border-color: #fe9f42 !important;
  border-radius: 16px;
  padding: 8px 12px;
  text-decoration: none;
  height: auto;
  flex-grow: initial;
  align-items: center;
  display: flex;
  max-height: 34px;
  transition-duration: 0.2s;

  &:hover {
    background-color: ${shade(0.2, '#FE9F42')} !important;
    color: white;
  }

  svg {
    margin-right: 12px;
  }
`

export default CreateNewProject
